/**
 * CalendarFormWizard.jsx
 * Formulario de Calendario em Etapas Premium
 *
 * 3 Passos:
 * 1. Basico - Nome, Tipo, Datas, Recorrencia (obrigatorio)
 * 2. Atributos - Atributos customizados (opcional)
 * 3. Revisao
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Tune as TuneIcon,
  CheckCircle as ReviewIcon,
} from '@mui/icons-material';
import { MuiFileInput } from 'mui-file-input';

import { useTranslation, useLocalization } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { calendarsActions } from '../../store';
import fetchOrThrow from '../../common/util/fetchOrThrow';
import { prefixString } from '../../common/util/stringUtils';

import PremiumWizard from '../../common/components/premium/PremiumWizard';
import PremiumInput from '../../common/components/premium/PremiumInput';
import EditAttributesAccordion from './EditAttributesAccordion';
import { getWizardColors } from '../../common/components/premium/WizardStepTemplates';

// ============================================================================
// CALENDAR HELPERS
// ============================================================================

const formatCalendarTime = (time) => {
  const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `TZID=${tzid}:${time.locale('en').format('YYYYMMDDTHHmmss')}`;
};

const parseRule = (rule) => {
  if (rule.endsWith('COUNT=1')) {
    return { frequency: 'ONCE' };
  }
  const fragments = rule.split(';');
  const frequency = fragments[0].substring(11);
  const by = fragments.length > 1 ? fragments[1].split('=')[1].split(',') : null;
  return { frequency, by };
};

const formatRule = (rule) => {
  const by = rule.by && rule.by.join(',');
  switch (rule.frequency) {
    case 'DAILY':
      return `RRULE:FREQ=${rule.frequency}`;
    case 'WEEKLY':
      return `RRULE:FREQ=${rule.frequency};BYDAY=${by || 'SU'}`;
    case 'MONTHLY':
      return `RRULE:FREQ=${rule.frequency};BYMONTHDAY=${by || 1}`;
    default:
      return 'RRULE:FREQ=DAILY;COUNT=1';
  }
};

const updateCalendar = (lines, index, element) =>
  window.btoa(lines.map((e, i) => (i !== index ? e : element)).join('\n'));

const simpleCalendar = () =>
  window.btoa(
    [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Traccar//NONSGML Traccar//EN',
      'BEGIN:VEVENT',
      'UID:00000000-0000-0000-0000-000000000000',
      `DTSTART;${formatCalendarTime(dayjs())}`,
      `DTEND;${formatCalendarTime(dayjs().add(1, 'hours'))}`,
      'RRULE:FREQ=DAILY',
      'SUMMARY:Event',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\n')
  );

// ============================================================================
// STEP 1: INFORMACOES BASICAS
// ============================================================================

const BasicInfoStep = ({ data, updateData, errors }) => {
  const t = useTranslation();
  const { htmlLang } = useLocalization();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const fromRef = useRef(null);
  const toRef = useRef(null);
  const [file, setFile] = useState(null);

  const decoded = data && data.data && window.atob(data.data);
  const simple = decoded && decoded.indexOf('//Traccar//') > 0;
  const lines = decoded && decoded.split('\n');
  const rule = simple && parseRule(lines[7]);

  const handleFileChange = (newFile) => {
    setFile(newFile);
    if (newFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const { result } = event.target;
        updateData({ data: result.substr(result.indexOf(',') + 1) });
      };
      reader.readAsDataURL(newFile);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <PremiumInput
            label={`${t('sharedName')} *`}
            value={data.name || ''}
            onChange={(e) => updateData({ name: e.target.value })}
            error={!!errors?.name}
            helperText={errors?.name}
            autoFocus
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" className="premium-input">
            <InputLabel>{t('sharedType')}</InputLabel>
            <Select
              label={t('sharedType')}
              value={simple ? 'simple' : 'custom'}
              onChange={(e) =>
                updateData({
                  data: e.target.value === 'simple' ? simpleCalendar() : null,
                })
              }
            >
              <MenuItem value="simple">{t('calendarSimple')}</MenuItem>
              <MenuItem value="custom">{t('reportCustom')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {simple ? (
        <>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('reportFrom')}
                value={dayjs(lines[5].slice(-15)).locale('en').format('YYYY-MM-DDTHH:mm')}
                onChange={(e) => {
                  const time = formatCalendarTime(dayjs(e.target.value, 'YYYY-MM-DDTHH:mm'));
                  updateData({
                    data: updateCalendar(lines, 5, `DTSTART;${time}`),
                  });
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  inputProps: { type: 'datetime-local', lang: htmlLang, style: { colorScheme: 'dark' } },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => {
                          const el = fromRef.current;
                          if (el) {
                            if (typeof el.showPicker === 'function') el.showPicker();
                            else el.focus();
                          }
                        }}
                      >
                        <CalendarIcon sx={{ color: colors.accent }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                inputRef={fromRef}
                className="premium-input"
                sx={{
                  '& input::-webkit-calendar-picker-indicator': {
                    display: 'none',
                    WebkitAppearance: 'none',
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('reportTo')}
                value={dayjs(lines[6].slice(-15)).locale('en').format('YYYY-MM-DDTHH:mm')}
                onChange={(e) => {
                  const time = formatCalendarTime(dayjs(e.target.value, 'YYYY-MM-DDTHH:mm'));
                  updateData({
                    data: updateCalendar(lines, 6, `DTEND;${time}`),
                  });
                }}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  inputProps: { type: 'datetime-local', lang: htmlLang, style: { colorScheme: 'dark' } },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        edge="end"
                        onClick={() => {
                          const el = toRef.current;
                          if (el) {
                            if (typeof el.showPicker === 'function') el.showPicker();
                            else el.focus();
                          }
                        }}
                      >
                        <CalendarIcon sx={{ color: colors.accent }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                inputRef={toRef}
                className="premium-input"
                sx={{
                  '& input::-webkit-calendar-picker-indicator': {
                    display: 'none',
                    WebkitAppearance: 'none',
                  },
                }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small" className="premium-input">
                <InputLabel>{t('calendarRecurrence')}</InputLabel>
                <Select
                  label={t('calendarRecurrence')}
                  value={rule.frequency}
                  onChange={(e) =>
                    updateData({
                      data: updateCalendar(lines, 7, formatRule({ frequency: e.target.value })),
                    })
                  }
                >
                  {['ONCE', 'DAILY', 'WEEKLY', 'MONTHLY'].map((it) => (
                    <MenuItem key={it} value={it}>
                      {t(prefixString('calendar', it.toLowerCase()))}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {['WEEKLY', 'MONTHLY'].includes(rule.frequency) && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small" className="premium-input">
                  <InputLabel>{t('calendarDays')}</InputLabel>
                  <Select
                    multiple
                    label={t('calendarDays')}
                    value={rule.by}
                    onChange={(e) =>
                      updateData({
                        data: updateCalendar(lines, 7, formatRule({ ...rule, by: e.target.value })),
                      })
                    }
                  >
                    {rule.frequency === 'WEEKLY'
                      ? ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(
                          (it) => (
                            <MenuItem key={it} value={it.substring(0, 2).toUpperCase()}>
                              {t(prefixString('calendar', it))}
                            </MenuItem>
                          )
                        )
                      : Array.from({ length: 31 }, (_, i) => i + 1).map((it) => (
                          <MenuItem key={it} value={String(it)}>
                            {it}
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </>
      ) : (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <MuiFileInput
              fullWidth
              placeholder={t('sharedSelectFile')}
              value={file}
              onChange={handleFileChange}
              className="premium-input"
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

// ============================================================================
// STEP 2: ATRIBUTOS
// ============================================================================

const AttributesStep = ({ data, updateData }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: '10px',
          backgroundColor: colors.surfaceHover,
          border: `1px solid ${colors.borderSubtle}`,
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '13px' }}>
          {t('wizardAttributesHelp')}
        </Typography>
      </Box>

      <EditAttributesAccordion
        attributes={data.attributes}
        setAttributes={(attributes) => updateData({ attributes })}
        definitions={{}}
      />
    </Box>
  );
};

// ============================================================================
// STEP 3: REVISAO
// ============================================================================

const ReviewStep = ({ data }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const colors = getWizardColors(tokens);

  const decoded = data && data.data && window.atob(data.data);
  const simple = decoded && decoded.indexOf('//Traccar//') > 0;

  const InfoRow = ({ label, value }) => {
    if (!value && value !== 0) return null;
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '11px' }}>
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.textPrimary, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  const attributeCount = data.attributes ? Object.keys(data.attributes).length : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 2,
          backgroundColor: colors.accentSubtle,
          borderRadius: tokens?.radius?.md || '8px',
          border: `1px solid ${colors.accent}30`,
        }}
      >
        <ReviewIcon sx={{ color: colors.accent }} />
        <Typography sx={{ color: colors.textPrimary }}>
          {t('wizardReviewConfirm')}
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.borderSubtle}`,
          p: 2.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            color: colors.textPrimary,
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: '11px',
            letterSpacing: '0.05em',
            mb: 2,
          }}
        >
          {t('sharedRequired')}
        </Typography>
        <InfoRow label={t('sharedName')} value={data.name} />
        <InfoRow label={t('sharedType')} value={simple ? t('calendarSimple') : t('reportCustom')} />
        <InfoRow label={t('sharedAttributes')} value={attributeCount > 0 ? `${attributeCount} ${t('sharedAttributes').toLowerCase()}` : '-'} />
      </Box>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CalendarFormWizard = ({ calendar, onSave, onCancel }) => {
  const t = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const steps = [
    {
      label: t('wizardStepBasic'),
      title: t('sharedRequired'),
      description: t('wizardCalendarBasicDescription') || 'Dados do calendario',
      icon: <CalendarIcon sx={{ fontSize: 18 }} />,
      component: BasicInfoStep,
      validate: (data) => {
        const errors = {};
        if (!data.name?.trim()) errors.name = t('sharedRequired');
        if (!data.id && !data.data) errors.data = t('sharedRequired');
        return Object.keys(errors).length ? errors : true;
      },
      getSummary: (data) => [
        { label: t('sharedName'), value: data.name },
      ],
    },
    {
      label: t('sharedAttributes'),
      title: t('sharedAttributes'),
      description: t('wizardAttributesDescription'),
      icon: <TuneIcon sx={{ fontSize: 18 }} />,
      component: AttributesStep,
      optional: true,
    },
    {
      label: t('wizardStepReview'),
      title: t('wizardReviewTitle'),
      description: t('wizardReviewDescription'),
      icon: <ReviewIcon sx={{ fontSize: 18 }} />,
      component: ReviewStep,
    },
  ];

  const handleComplete = async (data) => {
    try {
      const url = calendar?.id ? `/api/calendars/${calendar.id}` : '/api/calendars';
      const method = calendar?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const saved = await response.json();
        // Refresh calendars in Redux
        const calendarsResponse = await fetchOrThrow('/api/calendars');
        dispatch(calendarsActions.refresh(await calendarsResponse.json()));
        onSave?.(saved);
        navigate('/settings/calendars');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('errorGeneric'));
      }
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    navigate('/settings/calendars');
  };

  const initialData = calendar || {
    name: '',
    data: simpleCalendar(),
    attributes: {},
  };

  return (
    <PremiumWizard
      steps={steps}
      initialData={initialData}
      onComplete={handleComplete}
      onCancel={handleCancel}
      storageKey={calendar?.id ? null : 'calendar-wizard-draft'}
      completeButtonText={calendar?.id ? t('sharedSave') : t('sharedAdd')}
      nextButtonText={t('wizardNext')}
      backButtonText={t('sharedBack')}
      cancelButtonText={t('sharedCancel')}
      skipButtonText={t('wizardSkip')}
      showSummary
    />
  );
};

export default CalendarFormWizard;
